// Packages
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  useJsApiLoader,
  GoogleMap,
  Marker,
} from "@react-google-maps/api";
import { FormattedMessage } from "react-intl";
// Icons, Images etc.
import GmapMarker from "../../images/gmap-marker.svg";
// Contexts
import { LocaleContext } from "../../wrappers/localeWrapper";
import { EnvContext } from "../../components/envContext";

declare global {
  interface Window {
    google: any;
  }
}

interface MinimisedDeliveryAddressMapWidgetProps {
  lat: number;
  lng: number;
  onAreaChange: any;
  expandMap: any;
  addressMarked: any;
}

const mapOptions = {
  fullscreenControl: false,
  zoomControl: false,
  disableDoubleClickZoom: true,
  mapTypeControl: false,
  streetViewControl: false,
  gestureHandling: "none",
};

const LIBRARIES: ("places" | "geometry")[] = ["places", "geometry"];

// Inner component that renders when API is loaded
function MinimisedDeliveryAddressMapWidgetContent(props: MinimisedDeliveryAddressMapWidgetProps) {
  // Initialize markerPosition from props to ensure saved addresses show correct location
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number }>({
    lat: props.lat || 0,
    lng: props.lng || 0
  });
  const initialLoadDone = useRef(false);

  useEffect(() => {
    let lat = props.lat || 0.0;
    let lng = props.lng || 0.0;
    setMarkerPosition({ lat: lat, lng: lng });
  }, [props.lat, props.lng]);

  // Trigger initial geocoding when marker position is set
  useEffect(() => {
    if (!initialLoadDone.current && markerPosition.lat && markerPosition.lng && window.google?.maps) {
      initialLoadDone.current = true;
      let geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ latLng: markerPosition }, (responses: any) => {
        if (responses && responses[0]) {
          let streetName = responses[0].formatted_address;
          props.onAreaChange(streetName, markerPosition.lat, markerPosition.lng);
        }
      });
    }
  }, [markerPosition.lat, markerPosition.lng]);

  return (
    <GoogleMap
      mapContainerStyle={{
        height: "400px"
      }}
      center={markerPosition}
      zoom={17}
      options={mapOptions}
      onClick={props.expandMap}
    >
      {props.addressMarked ? (
        <Marker
          draggable={false}
          position={markerPosition}
          onClick={props.expandMap}
          icon={GmapMarker}
        />
      ) : null}
    </GoogleMap>
  );
}

// Wrapper component that handles loading the Google Maps API
function MinimisedDeliveryAddressMapWidget(props: MinimisedDeliveryAddressMapWidgetProps) {
  const { googleMapKey } = useContext(EnvContext);
  const localeContext = useContext(LocaleContext) as { locale: string; updateLocale: (locale: string) => void };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: googleMapKey || "",
    libraries: LIBRARIES,
    language: localeContext.locale,
  });

  if (!googleMapKey) {
    return null;
  }

  if (loadError) {
    return <div><FormattedMessage id="maps.error_loading" /></div>;
  }

  if (!isLoaded) {
    return <div style={{ height: "400px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <FormattedMessage id="maps.loading" />
    </div>;
  }

  return <MinimisedDeliveryAddressMapWidgetContent {...props} />;
}

export default React.memo(MinimisedDeliveryAddressMapWidget);
